import React, { useEffect, useState } from 'react';
import { FileDown, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi, ewbPdf, tallyApi } from '@/services/ewayBillApi';
import { fmtDT, ModeBadge, PartBPending, PartyCard, PrintedMark, StatusBadge, ValidLeft } from '@/components/ewayBill/shared';
import { ItemsTable } from '@/components/ewayBill/ItemsTable';
import { CancelEwbBox } from '@/components/ewayBill/CancelEwbBox';
import { TallyBillPanel } from '@/components/ewayBill/TallyBillPanel';
import { FixDistanceBox } from '@/components/ewayBill/FixDistanceBox';
import { KeyEwbBox } from '@/components/ewayBill/KeyEwbBox';

const Field = ({ l, v, testId }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-gray-500">{l}</div><div className="font-medium" data-testid={testId}>{v || '—'}</div></div>
);

const WRITE = { PENDING: ['Writing the EWB No into the Tally voucher…', 'text-sky-800 bg-sky-50 border-sky-200'],
  DONE: ['EWB No written into the Tally voucher', 'text-emerald-800 bg-emerald-50 border-emerald-200'],
  ERROR: ["Tally didn't take the EWB No", 'text-rose-800 bg-rose-50 border-rose-200'] };

/** Portal JSON route: JSON downloaded / results imported / Tally write-back; discard a bill still waiting for its number. */
const PortalInfo = ({ b, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);
  const w = b.tally?.ewb_write;
  const discard = () => {
    setBusy(true);
    tallyApi.discard(b.id).then((x) => { toast.success('E-way bill request discarded'); onChanged(x); })
      .catch((e) => toast.error(errMsg(e, 'Discard failed'))).finally(() => setBusy(false));
  };
  if (!b.portal_json && !b.portal_import && !b.manual_ewb && !w && !(b.route === 'portal' && b.can_discard)) return null;
  return (
    <div className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 text-sm" data-testid="ewb-portal-info">
      {b.portal_json && <p data-testid="ewb-portal-json-info">Portal JSON downloaded {fmtDT(b.portal_json.downloaded_at)} by {b.portal_json.by_name}</p>}
      {b.portal_import && <p data-testid="ewb-portal-import-info">EWB No imported from the portal file {b.portal_import.file} · {fmtDT(b.portal_import.at)} by {b.portal_import.by_name}</p>}
      {b.manual_ewb && <p data-testid="ewb-manual-ewb-info">EWB No typed in (made directly on the portal) by {b.manual_ewb.by_name} · {fmtDT(b.manual_ewb.at)}</p>}
      {w && <p className={`rounded-md border px-2 py-1 ${WRITE[w.status][1]}`} data-testid="ewb-tally-write">{WRITE[w.status][0]}{w.error ? ` — ${w.error}` : ''}</p>}
      {b.route === 'portal' && b.status === 'AWAITING' && <p className="text-gray-600">Tick this bill in the list → Download portal JSON → upload it on the portal (Bulk Generation) → import the results file.</p>}
      {b.distance_fix && <p className="text-xs text-gray-600" data-testid="ewb-distance-fix-info">Distance changed {b.distance_fix.old_km} → {b.distance_fix.km} km by {b.distance_fix.by_name} · {fmtDT(b.distance_fix.at)}</p>}
      {b.can_fix_distance && <FixDistanceBox b={b} onChanged={onChanged} />}
      {b.route === 'portal' && b.can_discard && (
        <Button size="sm" variant={sure ? 'destructive' : 'ghost'} className={sure ? '' : 'text-rose-700'} onClick={() => (sure ? discard() : setSure(true))} disabled={busy}
          data-testid="ewb-portal-discard-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}{sure ? 'Confirm discard' : 'Discard'}</Button>
      )}
    </div>
  );
};

/** One e-way bill: Part A + Part B, items, print and cancel. */
export const EwbDetailDialog = ({ billId, cfg, onClose, onChanged, refresh }) => {
  const [b, setB] = useState(null);
  useEffect(() => {
    setB(null);
    if (billId) ewbApi.get(billId).then(setB).catch((e) => { toast.error(errMsg(e, 'Failed to load')); onClose(); });
  }, [billId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (billId && refresh) ewbApi.get(billId).then(setB).catch(() => {});
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => ewbApi.get(billId).then((x) => { setB(x); onChanged(); }).catch((e) => toast.error(errMsg(e, 'Failed to load')));
  const pb = b?.part_b || {};
  return (
    <Dialog open={!!billId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ewb-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            E-Way Bill {b?.ewb_no ? <span className="font-mono" data-testid="ewb-detail-no">{b.ewb_no}</span>
              : <span className="text-base font-normal text-gray-400" data-testid="ewb-detail-no">{b ? 'not generated yet' : ''}</span>}
            {b && <><ModeBadge mode={b.mode} testId="ewb-detail-mode" /><StatusBadge b={b} testId="ewb-detail-status" /></>}
          </DialogTitle>
        </DialogHeader>
        {!b ? <Loader2 className="w-6 h-6 mx-auto my-10 animate-spin text-amber-600" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
              <Field l="Generated" v={fmtDT(b.ewb_date)} />
              <Field l="Valid until" v={<>{fmtDT(b.valid_upto)}<ValidLeft b={b} /></>} testId="ewb-detail-valid" />
              <Field l="Document" v={`${b.document_type} · ${b.doc_no}`} />
              <Field l="Doc date / type" v={`${b.doc_date} · ${b.doc_type_label}`} />
              <Field l="Supply / sub supply" v={`${b.supply_type || 'Outward'} · ${b.sub_supply_type}${b.sub_supply_desc ? ` — ${b.sub_supply_desc}` : ''}`} testId="ewb-detail-supply" />
              <Field l="Vehicle" v={<>{pb.vehicle_no || '—'}<PartBPending b={b} testId="ewb-detail-partb-pending" /></>} testId="ewb-detail-vehicle" />
              <Field l="Distance" v={`${pb.distance_km} km`} />
              <Field l="Created by" v={b.created_by_name} />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <PartyCard title="From (consignor)" p={b.from} testId="ewb-detail-from" />
              <PartyCard title="To (consignee)" p={b.to} testId="ewb-detail-to" />
            </div>
            <ItemsTable items={b.items} totals={b.totals} />
            {b.erp_note && <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-gray-600" data-testid="ewb-detail-erp-note">{b.erp_note}</p>}
            {b.route === 'tally' && <TallyBillPanel b={b} onChanged={(x) => { setB(x); onChanged(); }} onMapped={reload} />}
            <PortalInfo b={b} onChanged={(x) => { setB(x); onChanged(); }} />
            {b.cancel && (
              <p className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800" data-testid="ewb-detail-cancel-info">
                Cancelled {fmtDT(b.cancel.at)} by {b.cancel.by_name} — {b.cancel.reason}: {b.cancel.remark}
              </p>
            )}
            <div className="flex flex-wrap items-start gap-2 justify-end">
              {b.can_key_ewb && <KeyEwbBox b={b} onChanged={(x) => { setB(x); onChanged(); }} />}
              {b.can_cancel && <CancelEwbBox bill={b} reasons={cfg?.cancel_reasons || []} onDone={(x) => { setB({ ...b, ...x }); onChanged(); }} />}
              {b.ewb_no && (
                <div className="flex flex-col items-end">
                  <Button onClick={() => ewbPdf(b).then(reload).catch((e) => toast.error(errMsg(e, 'PDF failed')))} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ewb-detail-pdf">
                    <FileDown className="w-4 h-4 mr-1" />Print PDF
                  </Button>
                  <PrintedMark b={b} testId="ewb-detail-printed" />
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EwbDetailDialog;
